import { Box, Spinner } from '@chakra-ui/react';
import { PaginatedResponse, Params } from '@partiu/api/types';
import { cloneElement, useCallback, useEffect, useState } from 'react';
import { UseMutationOptions, UseMutationResult } from 'react-query';
import { useTheme } from '../../contexts/ThemeProvider';
import { useSearchParams } from 'react-router-dom';

const order = [
  'Época Negócios',
  'Pequenas Empresas & Grandes Negócios',
  'Galileu',
  'Marie Claire',
  'Auto Esporte',
  'Globo Rural',
];

export function InfiniteScroll<T>({
  mutationHook,
  renderItem,
  emptyResultsComponent = <></>,
  keyExtraction,
  scrollPercentageTrigger = 0.8,
  children,
  filterMagazine = false,
  hookProps = {},
}: IInfiniteScrollProps<T>) {
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { colors } = useTheme();
  const [resultDict, setResultDict] = useState<Resource<number, T[]>>({});
  const [done, setDone] = useState(false);

  const [refetch, setRefetch] = useState(false);

  const [categoriesSave, setCategoriesSave] = useState(JSON.stringify(hookProps));

  const [params] = useSearchParams();

  const mutation = mutationHook({
    onSuccess(data) {
      const newDict = page === 1 ? {} : { ...resultDict };

      let ordenerdResults = data.results;
      if (filterMagazine) {
        const grouped: Record<string, T[]> = {};
        data.results.forEach((item: any) => {
          const name = item.name || '';
          if (!grouped[name]) grouped[name] = [];
          grouped[name].push(item);
        });

        ordenerdResults = [];
        let added = true;
        while (added) {
          added = false;
          for (const magazine of order) {
            if (grouped[magazine] && grouped[magazine].length > 0) {
              ordenerdResults.push(grouped[magazine].shift() as T);
              added = true;
            }
          }
        }
      }

      newDict[page] = [...ordenerdResults];
      setHasNextPage(!!data.next);
      setResultDict(newDict);
      setIsInitialLoading(false);
    },
    onError() {
      setDone(true);
      setIsInitialLoading(false);
    },
  });
  const onScroll = useCallback(() => {
    const scroll = window.scrollY + window.innerHeight;
    const scrollHeight = document.body.scrollHeight;
    const percentage = scroll / scrollHeight;

    if (mutation.isLoading || done) return;
    if (refetch) return;

    if (percentage >= scrollPercentageTrigger && hasNextPage) {
      setRefetch(true);
      setPage(page + 1);
      setTimeout(() => {
        setRefetch(false);
      }, 2000);
    }
  }, [page, mutation.isLoading, refetch, done, hasNextPage, scrollPercentageTrigger]);

  useEffect(() => {
    document.addEventListener('scroll', onScroll);
    return () => document.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  useEffect(() => {
    if (params.size === 0 && isInitialLoading) {
      mutation.mutate({ page: 1, ...hookProps });
    }
  }, []);

  useEffect(() => {
    const newCategories = JSON.stringify(hookProps);
    if (categoriesSave !== newCategories) {
      setCategoriesSave(newCategories);
      setPage(1);
      setDone(false);
      setIsInitialLoading(true);
    }
  }, [hookProps]);

  useEffect(() => {
    if (!isInitialLoading && page === 1) return;

    mutation.mutate({ page, ...hookProps });
  }, [page, categoriesSave]);

  const results = Object.values(resultDict).reduce((p: T[], c: T[] = []) => [...p, ...c], []);

  if (isInitialLoading)
    return (
      <Box w={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Spinner color={colors.primaryColor} size={'lg'} />
      </Box>
    );

  if (!mutation.isLoading && results.length === 0) return emptyResultsComponent;

  return (
    <>
      {children(results.map((r: T) => cloneElement(renderItem(r), { key: keyExtraction(r) })))}
      {mutation.isLoading && (
        <Box w="100%" display="flex" justifyContent="center" py={4}>
          <Spinner color={colors.primaryColor} size="md" />
        </Box>
      )}
    </>
  );
}

interface IInfiniteScrollProps<T> {
  mutationHook: (
    options: UseMutationOptions<PaginatedResponse<T>, unknown, Params, unknown>
  ) => UseMutationResult<PaginatedResponse<T>, unknown, Params, unknown>;
  renderItem: (item: T) => JSX.Element;
  scrollPercentageTrigger?: number;
  children: (items: JSX.Element[]) => JSX.Element;
  hookProps?: Params;
  keyExtraction: (t: T) => string;
  emptyResultsComponent?: JSX.Element;
  filterMagazine?: boolean;
}
